'use client'

import { useCallback, useSyncExternalStore } from 'react'
import { createAuthClient, getAuthSessionSnapshot, subscribeAuthStateChange } from '@/lib/auth/client'

const authClient = createAuthClient()

export function useAuth() {
  const session = useSyncExternalStore(
    (onStoreChange) => {
      const subscription = subscribeAuthStateChange(() => onStoreChange())
      return () => subscription.unsubscribe()
    },
    getAuthSessionSnapshot,
    () => null
  )

  const signIn = useCallback(async (email: string, password: string) => {
    const { data } = await authClient.auth.signInWithPassword({ email, password })
    return data
  }, [])

  const signUp = useCallback(
    async (email: string, password: string, username: string, displayName?: string) => {
      const { data } = await authClient.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            display_name: displayName || username,
            first_access: true,
            onboarding_completed: false,
          },
        },
      })
      return data
    },
    []
  )

  const signOut = useCallback(async () => {
    await authClient.auth.signOut()
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    await authClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/settings/account`,
    })
  }, [])

  const updateUser = useCallback(
    async (payload: { email?: string; password?: string; data?: Record<string, unknown> }) => {
      const { data } = await authClient.auth.updateUser(payload)
      return data
    },
    []
  )

  return {
    user: session?.user ?? null,
    session,
    loading: false,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUser,
  }
}
